import { create } from 'zustand';
import { servicesContainer } from '@/core/di/services-container';
import { ApplicationLanguage } from '@/features/translations/types/language-management-types';
import { ApplicationType } from '@/features/translations/types/translation-types';

/**
 * UI Language type - mapped from API Language for i18n use
 * Uses consistent naming with our i18n types
 */
export interface UiLanguage {
  /** Language ID from API */
  id: number;
  /** ISO language code (e.g., 'en', 'he', 'es') */
  code: string;
  /** English name of the language */
  name: string;
  /** Native name of the language (e.g., 'עברית' for Hebrew) */
  nativeName: string;
  /** Text direction: 'ltr' or 'rtl' */
  direction: 'ltr' | 'rtl';
  /** Sort order for display */
  sortOrder: number;
  /** Whether the language is active */
  isActive: boolean;
  /** Optional flag/icon URL */
  imageUrl?: string;
}

/**
 * Map Application Language to UI Language
 */
function mapApplicationLanguageToUiLanguage(appLang: ApplicationLanguage): UiLanguage {
  return {
    id: appLang.languageId,
    code: appLang.languageCode,
    name: appLang.languageName,
    nativeName: appLang.nativeName,
    direction: appLang.isRtl ? 'rtl' : 'ltr',
    sortOrder: appLang.sortOrder,
    isActive: appLang.isActive,
  };
}

/**
 * i18n Languages Store State
 */
interface I18nLanguagesState {
  /** List of available languages from API */
  languages: UiLanguage[];
  /** Whether languages are being loaded */
  isLoading: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /** Whether languages have been fetched at least once */
  hasFetched: boolean;
}

/**
 * i18n Languages Store Actions
 */
interface I18nLanguagesActions {
  /** Fetch languages from API for a specific application type */
  fetchLanguages: (applicationType?: ApplicationType) => Promise<void>;
  /** Get a language by code */
  getLanguageByCode: (code: string) => UiLanguage | undefined;
  /** Check if a language code is RTL */
  isRtlLanguage: (code: string) => boolean;
  /** Clear error state */
  clearError: () => void;
  /** Reset store to initial state */
  reset: () => void;
}

type I18nLanguagesStore = I18nLanguagesState & I18nLanguagesActions;

/**
 * Initial state
 */
const initialState: I18nLanguagesState = {
  languages: [],
  isLoading: false,
  error: null,
  hasFetched: false,
};

/**
 * i18n Languages Store
 * Fetches and caches available languages from the translation API
 * Used by LanguageSwitcher component for dynamic language options
 */
export const useI18nLanguagesStore = create<I18nLanguagesStore>((set, get) => ({
  ...initialState,

  fetchLanguages: async (applicationType: ApplicationType = ApplicationType.ReactWeb) => {
    const { hasFetched, isLoading } = get();

    // Skip if already fetching
    if (isLoading) {
      return;
    }

    // Skip if already fetched successfully (cache)
    // Can be manually reset if needed
    if (hasFetched && get().languages.length > 0) {
      return;
    }

    set({ isLoading: true, error: null });

    try {
      const services = servicesContainer.getServices();
      const response = await services.getApplicationLanguagesUseCase.execute(applicationType);

      if (response.isSuccess && response.languages) {
        // Map application languages to UI languages and sort by sortOrder
        const uiLanguages = response.languages
          .filter((lang) => lang.isActive)
          .map(mapApplicationLanguageToUiLanguage)
          .sort((a, b) => a.sortOrder - b.sortOrder);

        set({
          languages: uiLanguages,
          isLoading: false,
          hasFetched: true,
        });

        if (import.meta.env.DEV) {
          console.log(`[i18n] Loaded ${uiLanguages.length} languages for app type ${applicationType} (${response.applicationName})`);
        }
      } else {
        throw new Error(response.errorMessage || 'Failed to fetch application languages');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch languages';
      console.error('[i18n] Error fetching application languages:', error);
      set({
        error: errorMessage,
        isLoading: false,
        hasFetched: true, // Mark as fetched even on error to prevent infinite retries
      });
    }
  },

  getLanguageByCode: (code: string) => {
    const { languages } = get();
    // Handle case-insensitive comparison and locale variants (e.g., 'en-US' matches 'en')
    const normalizedCode = code.toLowerCase();
    return (
      languages.find((l) => l.code.toLowerCase() === normalizedCode) ||
      languages.find((l) => l.code.toLowerCase().startsWith(normalizedCode.split('-')[0]))
    );
  },

  isRtlLanguage: (code: string) => {
    const lang = get().getLanguageByCode(code);
    return lang?.direction === 'rtl';
  },

  clearError: () => set({ error: null }),

  reset: () => set(initialState),
}));
